// SketchColorPicker.jsx
// Color picker that uses the SketchPicker from react-colorful.

// Copyright HS Analysis GmbH, 2024
// Author: Viktor Eberhardt

// Framework imports
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";

// External imports
import { makeStyles } from "@mui/styles";
import { HexColorPicker } from "react-colorful";

// Mui imports
import { presetColors } from "../utils/RandomColorGenerator";

/**
 * Color picker that uses the SketchPicker from react-colorful.
 * @param {string} color The color to display as a hex string.
 * @param {function} handleChange Callback function that receives the new color as a hex string.
 * @param {function} onClose Callback function that is called when the color picker is closed.
 * @param {bool} includeWhite Flag if the color white should be included in the color swatches.
 * @param {bool} interactive Flag if the color picker can be changed interactively.
 * @param {('click'|'doubleClick')} openOn How the picker should be opened; defaults to 'click'.
 * @param {function} onSwatchSingleClick Optional callback to invoke on single-click when openOn="doubleClick"; used to toggle parent selection.
 * @returns
 */
const SketchColorPicker = (props) => {
  const {
    color,
    handleChange,
    onClose,
    includeWhite,
    interactive = true,
    openOn = "click",
    onSwatchSingleClick,
  } = props;
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [xPosition, setXPosition] = useState(0);
  const [yPosition, setYPosition] = useState(0);
  const clickTimeoutRef = useRef(null);
  const [hexInput, setHexInput] = useState(color || "#ffffff");

  // Utility: normalize various hex formats to #RRGGBB (6-digit only; no alpha)
  const normalizeHex = (val) => {
    if (!val) return null;
    let v = ("" + val).trim().replace(/^#/i, "");
    // 3-digit shorthand -> expand to 6
    if (/^[0-9a-fA-F]{3}$/.test(v)) {
      v = v
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }
    // If 8 digits were provided, drop alpha and accept first 6 only
    if (/^[0-9a-fA-F]{8}$/.test(v)) {
      v = v.substring(0, 6);
    }
    // Accept 6 digits only
    if (/^[0-9a-fA-F]{6}$/.test(v)) return "#" + v.toLowerCase();
    return null;
  };

  // Keep input synced with external color when it changes
  useEffect(() => {
    const norm = normalizeHex(color);
    if (norm && norm !== hexInput) {
      setHexInput(norm);
    }
  }, [color]);

  let colorsForSwatches = presetColors;
  if (includeWhite) {
    colorsForSwatches = ["#ffffff", ...presetColors];
  }

  const openPickerAtEvent = (e) => {
    let x = e.pageX;
    let y = e.pageY;

    const pickerWidth = 220; // approximate width of the color picker
    const pickerHeight = 300; // approximate height of the color picker

    if (y + pickerHeight > window.innerHeight) {
      y = window.innerHeight - pickerHeight - 10; // 10px margin
    }

    if (x + pickerWidth > window.innerWidth) {
      x = window.innerWidth - pickerWidth - 10; // 10px margin
    }

    setXPosition(x);
    setYPosition(y);
    setDisplayColorPicker(true && interactive);
  };

  // Open on single click (default)
  const handleClick = (e) => {
    if (openOn === "click") {
      openPickerAtEvent(e);
      // Prevent parent toggles when the picker opens on click
      e.stopPropagation();
      return;
    }

    // When openOn is doubleClick, we want single click to toggle the parent control
    // but NOT when a double click follows. We stop propagation immediately and
    // schedule a deferred callback that toggles after a short delay unless a double click occurs.
    if (openOn === "doubleClick") {
      e.stopPropagation();
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      clickTimeoutRef.current = setTimeout(() => {
        clickTimeoutRef.current = null;
        if (typeof onSwatchSingleClick === "function") {
          onSwatchSingleClick();
        }
      }, 220);
    }
  };

  // Optional open on double click; prevents single-click behavior
  const handleDoubleClick = (e) => {
    if (openOn === "doubleClick") {
      // Cancel a pending single-click toggle and open the picker instead
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      openPickerAtEvent(e);
    }
    e.stopPropagation();
  };

  const handleClose = (e) => {
    setDisplayColorPicker(false);
    e.stopPropagation();
    onClose();
  };

  const classes = useStyles();

  return (
    <div className={classes.popover}>
      <div
        className={classes.mainSwatch}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <div className={classes.color} style={{ background: color }} />
      </div>
      {displayColorPicker &&
        ReactDOM.createPortal(
          [
            <div
              key={"close"}
              className={classes.cover}
              onClick={handleClose}
            />,
            <div
              key={"picker"}
              className={classes.pickerContainer}
              style={{ top: yPosition - 25, left: xPosition - 10 }}
            >
              <HexColorPicker
                className="color-picker"
                color={color}
                onChange={(val) => {
                  const norm = normalizeHex(val);
                  if (norm) handleChange(norm);
                }}
              />
              <div className={classes.pickerHexRow}>
                <span className={classes.pickerHexHash}>#</span>
                <input
                  aria-label="hex color"
                  className={
                    normalizeHex(hexInput)
                      ? classes.pickerHexInput
                      : `${classes.pickerHexInput} ${classes.pickerHexInputInvalid}`
                  }
                  value={hexInput.replace(/^#/i, "")}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = "#" + raw; // keep user input as-is (without sanitizing), prefixed with '#'
                    setHexInput(next);
                    const norm = normalizeHex(raw);
                    if (norm) {
                      handleChange(norm);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const norm = normalizeHex(hexInput);
                      if (norm) handleChange(norm);
                    }
                    e.stopPropagation();
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className={classes.pickerSwatches}>
                {colorsForSwatches.map((presetColor) => (
                  <button
                    key={presetColor}
                    className={classes.pickerSwatch}
                    style={{ background: presetColor }}
                    onClick={(e) => {
                      handleChange(presetColor);
                      e.stopPropagation();
                    }}
                  />
                ))}
              </div>
            </div>,
          ],
          document.body
        )}
    </div>
  );
};

SketchColorPicker.propTypes = {
  color: PropTypes.string.isRequired,
  handleChange: PropTypes.func,
  onClose: PropTypes.func,
  includeWhite: PropTypes.bool,
  interactive: PropTypes.bool,
  openOn: PropTypes.oneOf(["click", "doubleClick"]),
  onSwatchSingleClick: PropTypes.func,
};

SketchColorPicker.defaultProps = {
  handleChange: () => {},
  onClose: () => {},
  includeWhite: false,
  openOn: "click",
};

export default SketchColorPicker;

const useStyles = makeStyles({
  popover: {
    position: "relative",
  },
  pickerContainer: {
    position: "fixed",
    zIndex: "10000000",
    marginRight: "10px",
    padding: "16px",
    borderRadius: "5px",
    background: "#33333a",
    boxShadow: "0 6px 12px #999",
  },
  pickerHexRow: {
    display: "flex",
    alignItems: "center",
    marginTop: 12,
  },
  pickerHexHash: {
    color: "#ccc",
    fontFamily: "monospace",
    marginRight: 6,
  },
  pickerHexInput: {
    flex: 1,
    background: "#2b2b30",
    color: "#fff",
    border: "1px solid #555",
    borderRadius: 4,
    padding: "6px 8px",
    fontFamily: "monospace",
    outline: "none",
  },
  pickerHexInputInvalid: {
    borderColor: "#e57373",
    boxShadow: "0 0 0 1px #e57373 inset",
  },
  color: {
    width: "36px",
    height: "14px",
    borderRadius: "2px",
  },
  mainSwatch: {
    padding: "5px",
    background: "#fff",
    borderRadius: "1px",
    boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
    display: "inline-block",
    cursor: "pointer",
  },
  cover: {
    position: "fixed",
    top: "0px",
    right: "0px",
    bottom: "0px",
    left: "0px",
    zIndex: "1",
  },
  pickerSwatches: {
    display: "flex",
    flexWrap: "wrap",
    paddingTop: "12px",
    justifyContent: "center",
  },
  pickerSwatch: {
    width: "20px",
    aspectRatio: "1",
    margin: "4px",
    border: "none",
    padding: 0,
    borderRadius: "4px",
    cursor: "pointer",
    outline: "none",
  },
});
